import { ProductSliderSources } from "@omni/types/components/product/enums/productSliderResource.enum";
import { ControlsPosition } from "@omni/types/components/props/productSlider.props";
import { BreakpointMinSizes } from "@omni/types/enums/breakPoint.enum";
import { LayoutSettings } from "@omni/types/packages/swiper.types";
import { isDefined } from "@omni/utils/helper.utils";
import {
    createContext,
    Dispatch,
    ReactNode,
    RefObject,
    SetStateAction,
    use
} from "react";
import { Swiper as SwiperCore } from "swiper/types";

type ProductSliderContextValue = {
    title?: string | null;
    controlsPosition: ControlsPosition;
    source: ProductSliderSources;
    breakpointsConfig: {
        layoutSettings: LayoutSettings;
        hidden: Array<BreakpointMinSizes>;
    };
    productTileConfig: {
        allowInstantAddToCart: boolean;
        amountOfFeatures: number;
        showButton: boolean;
        showCategoryLink: boolean;
        useInsidePadding: boolean;
    };
    analyticsContext?: {
        list: string;
        listTitle: string;
    };
    mainSwiper: SwiperCore | null;
    allowPrev: boolean;
    allowNext: boolean;
    prevButtonRef: RefObject<HTMLButtonElement | null>;
    nextButtonRef: RefObject<HTMLButtonElement | null>;
    setMainSwiper: Dispatch<SetStateAction<SwiperCore | null>>;
    setAllowPrev: Dispatch<SetStateAction<boolean>>;
    setAllowNext: Dispatch<SetStateAction<boolean>>;
};

type ProductSliderContextProviderProps = {
    title?: string | null;
    controlsPosition: ControlsPosition;
    source: ProductSliderSources;
    breakpointsConfig: {
        layoutSettings: LayoutSettings;
        hidden: Array<BreakpointMinSizes>;
    };
    productTileConfig: {
        allowInstantAddToCart: boolean;
        amountOfFeatures: number;
        showButton: boolean;
        showCategoryLink: boolean;
        useInsidePadding: boolean;
    };
    mainSwiper: SwiperCore | null;
    allowPrev: boolean;
    allowNext: boolean;
    prevButtonRef: RefObject<HTMLButtonElement | null>;
    nextButtonRef: RefObject<HTMLButtonElement | null>;
    setMainSwiper: Dispatch<SetStateAction<SwiperCore | null>>;
    setAllowPrev: Dispatch<SetStateAction<boolean>>;
    setAllowNext: Dispatch<SetStateAction<boolean>>;
    children: ReactNode;
};

export const ProductSliderContext = createContext<
    ProductSliderContextValue | undefined
>(undefined);
ProductSliderContext.displayName = "ProductSliderContext";

export const ProductSliderContextProvider = ({
    title,
    controlsPosition,
    source,
    breakpointsConfig,
    productTileConfig,
    mainSwiper,
    allowPrev,
    allowNext,
    prevButtonRef,
    nextButtonRef,
    setMainSwiper,
    setAllowPrev,
    setAllowNext,
    children
}: ProductSliderContextProviderProps) => {
    return (
        <ProductSliderContext
            value={{
                title: title,
                controlsPosition: controlsPosition,
                source: source,
                breakpointsConfig: breakpointsConfig,
                productTileConfig: productTileConfig,
                mainSwiper: mainSwiper,
                allowPrev: allowPrev,
                allowNext: allowNext,
                prevButtonRef: prevButtonRef,
                nextButtonRef: nextButtonRef,
                setMainSwiper: setMainSwiper,
                setAllowPrev: setAllowPrev,
                setAllowNext: setAllowNext
            }}
        >
            {children}
        </ProductSliderContext>
    );
};

export const useProductSliderContext = () => {
    const context = use<ProductSliderContextValue | undefined>(
        ProductSliderContext
    );

    if (!isDefined(context)) {
        throw new Error(`${ProductSliderContext.displayName} is undefined.`);
    }

    return context;
};
